import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap5';
import * as Yup from 'yup';
import { Formik, Form as FormikForm } from 'formik';

import routes from 'app/routes';
import { primary200, danger200 } from 'components/new_ui/Colors';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import Table from 'components/new_ui/Table';
import Modal from 'components/new_ui/Modal/Modal';
import HorizontalSelectField from 'components/new_ui/Form/HorizontalSelectField';
import { useContext as useTeamsContext } from 'components/Managers/Teams/Context';
import { useContext } from 'components/Managers/Members/Context';
import Statistics from './Statistics';

export default function Summary({ show, members, cancelCallback }) {
  const { state: stateTeams } = useTeamsContext();
  const { state: { finchOptions }, dispatch, actions } = useContext();
  const navigate = useNavigate();

  const newMembers = members.filter((member) => member.status === 'New');
  const terminatedMembers = members.filter((member) => member.status === 'Terminated');

  const columns = [
    {
      name: 'First Name',
      selector: (row) => row.first_name,
      sortable: true,
    },
    {
      name: 'Last Name',
      selector: (row) => row.last_name,
      sortable: true,
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
    },
  ];

  const conditionalRowStyles = [
    {
      when: (row) => finchOptions.synchImport && row.status === 'New',
      style: {
        backgroundColor: primary200,
      },
    },
    {
      when: (row) => finchOptions.synchImport && row.status === 'Terminated',
      style: {
        backgroundColor: danger200,
      },
    },
  ];

  const formSchema = Yup.object().shape({
    team: Yup.string().when('team', () => {
      if (newMembers.length > 0) {
        return Yup.string().typeError('Please, choose a team.');
      }
      return Yup.string().nullable().optional();
    }),
  }, [['team', 'team']]);

  const onSubmitHundler = (values, { setSubmitting, setErrors }) => {
    actions.finchImport(
      dispatch,
      values.team,
      members.map((member) => member.id),
      (statistics) => {
        setSubmitting(false);
        navigate(routes.managers.members.success, { state: { statistics } });
      },
      (errorData) => {
        const newErrors = {};
        errorData.errors.forEach((item) => {
          newErrors[item.source.pointer.replace('/data/attributes/', '')] = item.detail;
        });
        setErrors(newErrors);
        setSubmitting(false);
      },
    );
  };

  return (
    <Modal show={show} size="lg">
      <h4 className="mb-4">
        Members to
        {' '}
        {finchOptions.synchImport ? 'synchronize' : 'import'}
      </h4>

      <Formik
        validationSchema={formSchema}
        initialValues={{
          team: null,
        }}
        onSubmit={onSubmitHundler}
      >
        {({ isSubmitting }) => (
          <FormikForm className="needs-validation" noValidate="">
            <section>
              {newMembers.length > 0 && (
                <>
                  <p>
                    Please, select a team for your members.
                  </p>

                  <Row>
                    <Col md="6">
                      <HorizontalSelectField
                        name="team"
                        label=""
                        options={
                        (stateTeams.teams || []).map((team) => ({
                          label: team.attributes.title,
                          value: team.id,
                        }))
                      }
                        isLoading={stateTeams.teams === null}
                        required={newMembers.length > 0}
                      />
                    </Col>
                  </Row>
                </>
              )}

              <Statistics
                newMembersCount={newMembers.length}
                terminatedMembersCount={terminatedMembers.length}
                totalMembersCount={members.length - terminatedMembers.length}
              />

              <Table
                columns={columns}
                data={members}
                selectableRows={false}
                conditionalRowStyles={conditionalRowStyles}
              />
            </section>

            <footer>
              <Button
                variant="outline-primary"
                onClick={() => cancelCallback()}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting || members.length === 0 || (newMembers.length > 0 && stateTeams.teams === null)}
              >
                {isSubmitting && <LoadingPlaceholder placement="inline" text=" " />}
                {finchOptions.synchImport ? 'Synchronize' : 'Import'}
              </Button>
            </footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
}

Summary.propTypes = {
  show: PropTypes.bool.isRequired,
  members: PropTypes.arrayOf(PropTypes.object).isRequired,
  cancelCallback: PropTypes.func.isRequired,
};
